import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from './client'

// ============================================
// Types
// ============================================

export interface QueueWord {
  word: string
  sense_count: number
}

export interface QueueResponse {
  words: QueueWord[]
  total: number
}

export interface WordSense {
  id: string
  meaning: string
  translations: string[]
  example?: string
  status: 'available' | 'skipped' | 'has_card'
}

export interface WordDetail {
  word: string
  ipa: string
  audio_url?: string
  senses: WordSense[]
}

export interface SearchResult {
  word: string
  meaning: string
  sense_count: number
}

export interface PriorityWord {
  word: string
  sense_count: number
  created_at: string
}

export interface PriorityResponse {
  language: string
  words: PriorityWord[]
  count: number
}

interface SkipRequest {
  language: string
  word: string
  sense_id?: string
}

interface SkipResponse {
  skipped: boolean
  word: string
  skip_id: number
}

// ============================================
// Query Keys
// ============================================

export const queueKeys = {
  all: ['queue'] as const,
  browser: (language: string) => [...queueKeys.all, 'browser', language] as const,
  priority: (language: string) => [...queueKeys.all, 'priority', language] as const,
  detail: (language: string, word: string) => [...queueKeys.all, 'detail', language, word] as const,
  search: (language: string, query: string) =>
    [...queueKeys.all, 'search', language, query] as const,
}

// ============================================
// Hooks
// ============================================

/**
 * Hook to fetch the queue browser data (flat list of words in frequency order)
 */
export function useQueueBrowser(language: string) {
  return useQuery({
    queryKey: queueKeys.browser(language),
    queryFn: () => api.get<QueueResponse>(`/api/queue/browser?language=${language}`),
    enabled: Boolean(language),
    staleTime: 30_000, // 30 seconds
  })
}

/**
 * Hook to fetch word details with all senses
 */
export function useWordDetail(language: string, word: string) {
  return useQuery({
    queryKey: queueKeys.detail(language, word),
    queryFn: () => api.get<WordDetail>(`/api/queue/${language}/${encodeURIComponent(word)}`),
    enabled: Boolean(language) && Boolean(word),
    staleTime: 60_000, // 1 minute
  })
}

/**
 * Hook to search words in the dictionary
 */
export function useSearchQueue(language: string, query: string) {
  return useQuery({
    queryKey: queueKeys.search(language, query),
    queryFn: () =>
      api.get<{ words: SearchResult[] }>(
        `/api/queue/search?language=${language}&q=${encodeURIComponent(query)}`
      ),
    enabled: Boolean(language) && query.length >= 2,
    staleTime: 30_000,
  })
}

/**
 * Hook to skip a word or sense with optimistic update and undo support
 */
export function useSkipWord() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: SkipRequest) => {
      if (data.sense_id) {
        // Skip specific sense
        return api.post<SkipResponse, { language: string; word: string; sense_id: number }>(
          '/api/queue/skip-sense',
          {
            language: data.language,
            word: data.word,
            sense_id: parseInt(data.sense_id, 10),
          }
        )
      }
      // Skip entire word
      return api.post<SkipResponse, { language: string; word: string }>('/api/queue/skip-word', {
        language: data.language,
        word: data.word,
      })
    },
    onMutate: async (variables) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queueKeys.browser(variables.language),
      })

      // Snapshot previous value
      const previousQueue = queryClient.getQueryData<QueueResponse>(
        queueKeys.browser(variables.language)
      )

      // Optimistically update - remove word from list
      if (!variables.sense_id && previousQueue) {
        const updatedQueue: QueueResponse = {
          ...previousQueue,
          words: previousQueue.words.filter((w) => w.word !== variables.word),
          total: previousQueue.total - 1,
        }
        queryClient.setQueryData(queueKeys.browser(variables.language), updatedQueue)
      }

      // If skipping a sense, update the word detail
      if (variables.sense_id) {
        const previousDetail = queryClient.getQueryData<WordDetail>(
          queueKeys.detail(variables.language, variables.word)
        )
        if (previousDetail) {
          const updatedDetail: WordDetail = {
            ...previousDetail,
            senses: previousDetail.senses.map((s) =>
              s.id === variables.sense_id ? { ...s, status: 'skipped' as const } : s
            ),
          }
          queryClient.setQueryData(
            queueKeys.detail(variables.language, variables.word),
            updatedDetail
          )
        }
      }

      return { previousQueue }
    },
    onError: (_error, variables, context) => {
      // Rollback on error
      if (context?.previousQueue) {
        queryClient.setQueryData(queueKeys.browser(variables.language), context.previousQueue)
      }
    },
    onSettled: (_data, _error, variables) => {
      // Refetch to ensure consistency
      void queryClient.invalidateQueries({
        queryKey: queueKeys.browser(variables.language),
      })
      if (variables.sense_id) {
        void queryClient.invalidateQueries({
          queryKey: queueKeys.detail(variables.language, variables.word),
        })
      }
    },
  })
}

interface RestoreRequest {
  skipId: number
  language: string
  word: string
  sense_id?: string
}

/**
 * Hook to restore a skipped word or sense
 */
export function useRestoreWord() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: RestoreRequest) =>
      api.delete<{ restored: boolean }>(`/api/queue/skipped/${data.skipId}`),
    onMutate: (variables) => {
      // If restoring a sense, optimistically update
      if (variables.sense_id) {
        const previousDetail = queryClient.getQueryData<WordDetail>(
          queueKeys.detail(variables.language, variables.word)
        )
        if (previousDetail) {
          const updatedDetail: WordDetail = {
            ...previousDetail,
            senses: previousDetail.senses.map((s) =>
              s.id === variables.sense_id ? { ...s, status: 'available' as const } : s
            ),
          }
          queryClient.setQueryData(
            queueKeys.detail(variables.language, variables.word),
            updatedDetail
          )
        }
        return { previousDetail }
      }
      return {}
    },
    onError: (_error, variables, context) => {
      // Rollback on error
      if (context?.previousDetail) {
        queryClient.setQueryData(
          queueKeys.detail(variables.language, variables.word),
          context.previousDetail
        )
      }
    },
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queueKeys.browser(variables.language),
      })
      void queryClient.invalidateQueries({
        queryKey: queueKeys.detail(variables.language, variables.word),
      })
    },
  })
}

// ============================================
// Priority Hooks
// ============================================

/**
 * Hook to fetch priority words
 */
export function usePriorityWords(language: string) {
  return useQuery({
    queryKey: queueKeys.priority(language),
    queryFn: () => api.get<PriorityResponse>(`/api/queue/priority?language=${language}`),
    enabled: Boolean(language),
    staleTime: 30_000,
  })
}

interface AddPriorityRequest {
  language: string
  word: string
}

/**
 * Hook to add a word to priority queue
 */
export function useAddPriorityWord() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: AddPriorityRequest) =>
      api.post<{ added: boolean; word: string }, AddPriorityRequest>('/api/queue/priority', data),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: queueKeys.priority(variables.language),
      })

      const previousPriority = queryClient.getQueryData<PriorityResponse>(
        queueKeys.priority(variables.language)
      )

      // Optimistically add word to priority list
      if (previousPriority) {
        const newWord: PriorityWord = {
          word: variables.word,
          sense_count: 1, // Will be updated on refetch
          created_at: new Date().toISOString(),
        }
        queryClient.setQueryData(queueKeys.priority(variables.language), {
          ...previousPriority,
          words: [...previousPriority.words, newWord],
          count: previousPriority.count + 1,
        })
      }

      return { previousPriority }
    },
    onError: (_error, variables, context) => {
      if (context?.previousPriority) {
        queryClient.setQueryData(queueKeys.priority(variables.language), context.previousPriority)
      }
    },
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queueKeys.priority(variables.language),
      })
      void queryClient.invalidateQueries({
        queryKey: queueKeys.browser(variables.language),
      })
    },
  })
}

interface RemovePriorityRequest {
  language: string
  word: string
}

/**
 * Hook to remove a word from priority queue
 */
export function useRemovePriorityWord() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: RemovePriorityRequest) =>
      api.delete<{ removed: boolean; word: string }>(
        `/api/queue/priority/${encodeURIComponent(data.word)}?language=${data.language}`
      ),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: queueKeys.priority(variables.language),
      })

      const previousPriority = queryClient.getQueryData<PriorityResponse>(
        queueKeys.priority(variables.language)
      )

      // Optimistically remove word from priority list
      if (previousPriority) {
        queryClient.setQueryData(queueKeys.priority(variables.language), {
          ...previousPriority,
          words: previousPriority.words.filter((w) => w.word !== variables.word),
          count: previousPriority.count - 1,
        })
      }

      return { previousPriority }
    },
    onError: (_error, variables, context) => {
      if (context?.previousPriority) {
        queryClient.setQueryData(queueKeys.priority(variables.language), context.previousPriority)
      }
    },
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queueKeys.priority(variables.language),
      })
      void queryClient.invalidateQueries({
        queryKey: queueKeys.browser(variables.language),
      })
    },
  })
}
